import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { associateDeviceToCompany } from "../../../api/device";
import { ENUMDEVICETYPE_OPTION } from "../../../generated/enums";
import { Building } from "../../../generated/types";
import { Button } from "../../common/Button";
import { FormInput, FormSelect } from "../../common/FormLayout";
import { PasswordField } from "../../common/PasswordField";
import { SetupError } from "./SetupLayout";

/**
 * Types qui DOIVENT être installés dans un bâtiment, miroir de
 * `link.BuildingRequiredForType` côté backend — même liste que `AssociateDeviceModal`.
 */
const BUILDING_REQUIRED_TYPES = ["avitouch", "avibox", "labelbox"];

/** Le vigebox ne remonte pas de mesures par bâtiment : lui en proposer un n'a pas de sens. */
export const BUILDING_EXCLUDED_TYPES = ["vigebox"];

/**
 * Types identifiés par leur code UUID, sans mot de passe : le serenity n'est pas un
 * régulateur legacy, il ne porte ni numéro de série ni mot de passe imprimés.
 */
const UUID_TYPES = ["serenity"];

const EMPTY_FORM = { type: "", idBuilding: "", uuid: "", password: "" };

/**
 * Produits associés pendant la session, suivis du formulaire d'association d'un produit
 * libre (numéro de série + mot de passe). Partagé par la mise en route de l'éleveur et la
 * création d'élevage côté admin / PSC.
 */
export const AssociateDeviceForm = ({
  companyId,
  buildings,
  associated,
  onAssociated,
  hint = "",
  initialBuildingId = "",
}: {
  /** Ignoré par le backend pour un éleveur, qui impose l'entreprise de la session. */
  companyId: number;
  buildings: Building[];
  associated: string[];
  onAssociated: (uuid: string) => void;
  hint?: string;
  /** Bâtiment pré-choisi, quand on associe depuis un bâtiment donné. */
  initialBuildingId?: string;
}) => {
  const { t } = useTranslation();
  const emptyForm = { ...EMPTY_FORM, idBuilding: initialBuildingId };
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const buildingRequired = BUILDING_REQUIRED_TYPES.includes(form.type);
  const uuidOnly = UUID_TYPES.includes(form.type);
  // Tant qu'aucun type n'est choisi, on n'affiche rien : proposer un bâtiment qu'il
  // faudrait retirer au choix suivant ferait sauter le formulaire sous l'utilisateur.
  const buildingShown =
    form.type !== "" && !BUILDING_EXCLUDED_TYPES.includes(form.type);

  const changeType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const type = e.target.value;
    setForm((prev) => ({
      ...prev,
      type,
      // Un bâtiment choisi avant de basculer sur un type qui n'en prend pas partirait
      // quand même au backend. Même chose pour un mot de passe saisi avant de choisir
      // un serenity.
      idBuilding: BUILDING_EXCLUDED_TYPES.includes(type) ? "" : prev.idBuilding,
      password: UUID_TYPES.includes(type) ? "" : prev.password,
    }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.type || !form.uuid.trim() || (!uuidOnly && !form.password)) {
      setError(
        uuidOnly ? t("setup-error-device-uuid") : t("setup-error-device-fields")
      );
      return;
    }
    if (buildingRequired && !form.idBuilding) {
      setError(t("setup-error-device-building"));
      return;
    }

    setSubmitting(true);
    const uuid = form.uuid.trim();
    const result = await associateDeviceToCompany({
      id_company: companyId,
      uuid,
      type: form.type,
      password_legacy: form.password,
      // 0 = aucun bâtiment, accepté pour serenity et vigebox.
      id_building: Number(form.idBuilding) || 0,
    });
    setSubmitting(false);

    if (!result.success) {
      setError(result.error);
      return;
    }
    setForm(emptyForm);
    onAssociated(uuid);
  };

  return (
    <>
      {associated.length > 0 && (
        <ul className="mb-6 flex flex-col gap-2">
          {associated.map((uuid) => (
            <li
              key={uuid}
              className="flex items-center gap-3 bg-grey-light/50 rounded-lg px-4 py-2 text-default-text"
            >
              <FontAwesomeIcon icon="check-circle" />
              <span className="font-oxaniumMedium">{uuid}</span>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="space-y-4">
        <SetupError message={error} />

        {/* Le type précède le bâtiment : c'est lui qui décide si ce dernier est requis. */}
        <FormSelect
          label={t("setup-device-type")}
          value={form.type}
          onChange={changeType}
          options={ENUMDEVICETYPE_OPTION.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          required
        />

        {buildingShown && (
          <FormSelect
            label={t("setup-device-building")}
            value={form.idBuilding}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, idBuilding: e.target.value }))
            }
            options={buildings.map((building) => ({
              value: String(building.id),
              label: building.name,
            }))}
            required={buildingRequired}
          />
        )}

        <FormInput
          label={uuidOnly ? t("setup-device-uuid") : t("setup-device-serial")}
          value={form.uuid}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, uuid: e.target.value }))
          }
          required
          placeholder={uuidOnly ? "" : "132845901"}
        />
        {!uuidOnly && (
          <PasswordField
            label={t("setup-device-password")}
            value={form.password}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, password: e.target.value }))
            }
            required
          />
        )}

        {hint && <p className="text-sm text-grey-readable">{hint}</p>}

        <Button
          type="submit"
          variant="secondary"
          size="full"
          disabled={submitting}
        >
          {submitting ? t("setup-saving") : t("setup-devices-add")}
        </Button>
      </form>
    </>
  );
};
